import { useState, useEffect, useCallback, useMemo } from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import { logger } from '../../services/logger';
import apiClient from '../../services/api/apiClient';
import axios from 'axios';
import IAlertType from '../../utils/types/props/IAlertType';
import AlertComponent from '../../components/utility/AlertComponent';
import { Spinner } from 'react-bootstrap';
import { IPointDeVente } from '../../utils/types/IPointDeVente';
import { IAgent } from '../../utils/types/IAgent';
import PdvSelect from '../../components/select/PdvSelect';
import AgentGrid from '../../components/grid/AgentGrid';
import { Helmet } from 'react-helmet-async';
import SkeletonLoader from '../../components/loader/SkeletonLoader';
import { formatAxiosError } from '../../utils/formatAxiosError';
import TerminalGrid from '../../components/grid/TerminalGrid';
import { Terminal } from '../../utils/types/classes/Terminal';
import { convertDetailShiftDates, convertShiftDates, convertTerminalDates } from '../../utils/dateConverter';
import { VShift } from '../../utils/types/VShift';
import ShiftGrid from '../../components/grid/ShiftGrid';
import AgentSelect from '../../components/select/AgentSelect';
import ShiftSelect from '../../components/select/ShiftSelect';
import { FDetailShift } from '../../utils/types/FDetailShift';
import DetailShiftGrid from '../../components/grid/DetailShiftGrid';
import { TPompe } from '../../utils/types/tables/TPompe';
import { VPistolet } from '../../utils/types/tables/VPistolet';
import PompeGrid from '../../components/grid/PompeGrid';
import VPistoletGrid from '../../components/grid/VPistoletGrid';
import { useQuery } from '@tanstack/react-query';
import QueryErrorAlerts, { QueryErrorEntry } from '../../components/others/QueryErrorAlerts';

const PointOfSaleMasterDataPage = () => {
    const [selectedPdvId, setSelectedPdvId] = useState<number | null>(null);

    const [shiftStartDate, setShiftStartDate] = useState<string | null>(null);
    const [shiftEndDate, setShiftEndDate] = useState<string | null>(null);
    const [shiftOnlyOpen, setShiftOnlyOpen] = useState<boolean>(true);

    const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);
    const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);

    const [isGeneralDataLoading, setIsGeneralDataLoading] = useState<boolean>(false);
    const [isShiftDataLoading, setIsShiftDataLoading] = useState<boolean>(false);

    // ---

    const {
        data: pointsDeVente = [],
        isLoading: isLoadingPointsDeVente,
        isFetching: isFetchingPointsDeVente,
        refetch: refetchPointsDeVente,
        isError: isPointsDeVenteError,
        error: pointsDeVenteError,
    } = useQuery<IPointDeVente[]>({
        queryKey: ['pointsDeVente'],
        queryFn: async () => {
            const res = await apiClient.get('/api/pdv');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des points de vente' },
        staleTime: 1000 * 60 * 60, // 1 hour
    });

    const {
        data: shifts = [],
        isLoading: isLoadingShifts,
        isFetching: isFetchingShifts,
        refetch: refetchShifts,
        isError: isShiftsError,
        error: shiftsError,
    } = useQuery<VShift[]>({
        queryKey: [
            'shifts',
            {
                idPdv: selectedPdvId,
                onlyOpen: shiftOnlyOpen,
                dateDebut: shiftStartDate,
                dateFin: shiftEndDate,
            },
        ],
        queryFn: async () => {
            const res = await apiClient.get('/api/shift/getData', {
                params: {
                    idPdv: selectedPdvId,
                    onlyOpen: shiftOnlyOpen,
                    dateDebut: shiftStartDate,
                    dateFin: shiftEndDate,
                },
            });

            let fixedData = res.data.map((shift: VShift) => convertShiftDates(shift));
            return fixedData ?? [];
        },
        meta: { title: 'Chargement des shifts' },
        enabled: false,
    });

    const {
        data: agents = [],
        isLoading: isLoadingAgents,
        isFetching: isFetchingAgents,
        refetch: refetchAgents,
        isError: isAgentsError,
        error: agentsError,
    } = useQuery<IAgent[]>({
        queryKey: [
            'agents',
            {
                idPdv: selectedPdvId,
            },
        ],
        queryFn: async () => {
            const res = await apiClient.get('/api/agents/byPdv', {
                params: {
                    idPdv: selectedPdvId,
                },
            });

            return res.data ?? [];
        },
        meta: { title: 'Chargement des agents' },
        enabled: false,
        staleTime: 1000 * 60 * 60, // 1 hour
    });

    const {
        data: terminaux = [],
        isLoading: isLoadingTerminaux,
        refetch: refetchTerminaux,
        isError: isTerminauxError,
        error: terminauxError,
    } = useQuery<Terminal[]>({
        queryKey: [
            'terminaux',
            {
                idPdv: selectedPdvId,
            },
        ],
        queryFn: async () => {
            const res = await apiClient.get('/api/terminal/getData', {
                params: {
                    idPdv: selectedPdvId,
                },
            });

            let fixedData = res.data.map((terminal: Terminal) => convertTerminalDates(terminal));
            return fixedData ?? [];
        },
        meta: { title: 'Chargement des terminaux' },
        enabled: false,
            staleTime: 1000 * 60 * 60, // 1 hour
    });

    const {
        data: pompes = [],
        isLoading: isLoadingPompes,
        refetch: refetchPompes,
        isError: isPompesError,
        error: pompesError,
    } = useQuery<TPompe[]>({
        queryKey: [
            'pompes',
            {
                idPdv: selectedPdvId,
            },
        ],
        queryFn: async () => {
            const res = await apiClient.get('/api/pompes/byPdv', {
                params: {
                    idPdv: selectedPdvId,
                },
            });

            return res.data ?? [];
        },
        meta: { title: 'Chargement des pompes' },
        enabled: false,
        staleTime: 1000 * 60 * 60, // 1 hour
    });

    const {
        data: vPistolets = [],
        isLoading: isLoadingPistolets,
        refetch: refetchPistolets,
        isError: isPistoletsError,
        error: pistoletsError,
    } = useQuery<VPistolet[]>({
        queryKey: [
            'vPistolets',
            {
                idPdv: selectedPdvId,
            },
        ],
        queryFn: async () => {
            const res = await apiClient.get('/api/pistolets/vByPdv', {
                params: {
                    idPdv: selectedPdvId,
                },
            });

            return res.data ?? [];
        },
        meta: { title: 'Chargement des pistolets' },
        enabled: false,
    });

    const {
        data: detailsShift = [],
        isLoading: isLoadingDetailsShift,
        refetch: refetchDetailsShift,
        isError: isDetailsShiftError,
        error: detailsShiftError,
    } = useQuery<FDetailShift[]>({
        queryKey: [
            'detailsShift',
            {
                idShift: selectedShiftId,
                idAgent: selectedAgentId,
            },
        ],
        queryFn: async () => {
            const res = await apiClient.get('/api/shift/details-shifts', {
                params: {
                    idShift: selectedShiftId,
                    idAgent: selectedAgentId,
                },
            });

            let fixedData = res.data.map((detailShift: FDetailShift) => convertDetailShiftDates(detailShift));
            return fixedData ?? [];
        },
        meta: { title: 'Chargement des details du shift' },
        enabled: false,
    });

    // ---

    const handleLoadShifts = useCallback(() => {
        if (!selectedPdvId) return;
        return refetchShifts();
    }, [selectedPdvId, refetchShifts]);

    const handleLoadAgents = useCallback(() => {
        if (!selectedPdvId) return;
        return refetchAgents();
    }, [selectedPdvId, refetchAgents]);

    const handleLoadTerminaux = useCallback(() => {
        if (!selectedPdvId) return;
        return refetchTerminaux();
    }, [selectedPdvId, refetchTerminaux]);

    const handleLoadPompes = useCallback(() => {
        if (!selectedPdvId) return;
        return refetchPompes();
    }, [selectedPdvId, refetchPompes]);

    const handleLoadPistolets = useCallback(() => {
        if (!selectedPdvId) return;
        return refetchPistolets();
    }, [selectedPdvId, refetchPistolets]);

    const handleLoadDetailsShift = useCallback(() => {
        if (!selectedShiftId) return;
        return refetchDetailsShift();
    }, [selectedShiftId, refetchDetailsShift]);

    // ---

    const [systemAlerts, setSystemAlerts] = useState<{ type: IAlertType; message: string }[]>([]);
    const handleDismissAlert = (index: number) => {
        setSystemAlerts((prevAlerts) => prevAlerts.filter((_, i) => i !== index));
    };

    // ---

    const loadData = async () => {
        try {
            setIsGeneralDataLoading(true);
            await Promise.all([handleLoadShifts(), handleLoadAgents(), handleLoadTerminaux(), handleLoadPompes(), handleLoadPistolets()].filter(Boolean));
        } finally {
            setIsGeneralDataLoading(false);
        }
    };

    const loadShiftData = async () => {
        setIsShiftDataLoading(true);

        try {
            await Promise.all([handleLoadDetailsShift()]);
        } catch (err: any) {
            if (!axios.isCancel(err)) {
                setSystemAlerts((prevAlerts) => [
                    ...prevAlerts,
                    {
                        type: 'danger',
                        message: 'Erreur lors du chargement des données du shift. ' + formatAxiosError(err).message,
                    },
                ]);
            }

            logger.error(err);
        } finally {
            setIsShiftDataLoading(false);
        }
    };

    // ---

    useEffect(() => {
        if (pointsDeVente.length === 1) {
            setSelectedPdvId(pointsDeVente[0].id);
        }
    }, [pointsDeVente]);

    useEffect(() => {
        setSelectedShiftId(null);
    }, [selectedPdvId]);

    useEffect(() => {
        setSelectedAgentId(null);
    }, [selectedShiftId]);

    // ---

    const loading = useMemo(
        () => ({
            generalData: { title: 'Chargement des données générales', isLoading: isGeneralDataLoading },
            shiftData: { title: 'Chargement des données du shift', isLoading: isShiftDataLoading },
        }),
        [isGeneralDataLoading, isShiftDataLoading],
    );

    const queryErrors = useMemo<QueryErrorEntry[]>(
        () => [
            { isError: isPointsDeVenteError, error: pointsDeVenteError, title: 'Erreur lors du chargement des points de vente', refetch: refetchPointsDeVente },
            { isError: isShiftsError, error: shiftsError, title: 'Erreur lors du chargement des shifts', refetch: handleLoadShifts },
            { isError: isAgentsError, error: agentsError, title: 'Erreur lors du chargement des agents', refetch: handleLoadAgents },
            { isError: isTerminauxError, error: terminauxError, title: 'Erreur lors du chargement des terminaux', refetch: handleLoadTerminaux },
            { isError: isPompesError, error: pompesError, title: 'Erreur lors du chargement des pompes', refetch: handleLoadPompes },
            { isError: isPistoletsError, error: pistoletsError, title: 'Erreur lors du chargement des pistolets', refetch: handleLoadPistolets },
            { isError: isDetailsShiftError, error: detailsShiftError, title: 'Erreur lors du chargement des détails du shift', refetch: handleLoadDetailsShift },
        ],
        [isPointsDeVenteError, pointsDeVenteError, refetchPointsDeVente, isShiftsError, shiftsError, handleLoadShifts, isAgentsError, agentsError, handleLoadAgents, isTerminauxError, terminauxError, handleLoadTerminaux, isPompesError, pompesError, handleLoadPompes, isPistoletsError, pistoletsError, handleLoadPistolets, isDetailsShiftError, detailsShiftError, handleLoadDetailsShift],
    );

    // ---

    return (
        <>
            <Helmet>
                <title>POS - Master Data :: Portail PETROSIL</title>
            </Helmet>

            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <div className="page-pretitle">Listing</div>
                            <h2 className="page-title">POS - Master Data</h2>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">
                    <AlertComponent alerts={systemAlerts} onDismiss={handleDismissAlert} />
                    <QueryErrorAlerts queries={queryErrors} />

                    <div className="card">
                        <div className="accordion accordion-flush" id="accordion-flush">
                            <div className="accordion-item">
                                <button className="accordion-header" type="button" data-bs-toggle="collapse" data-bs-target="#collapse-1-flush" aria-expanded="true">
                                    <div className="accordion-header-text">
                                        <h4>Filtre</h4>
                                    </div>
                                    <div className="accordion-header-toggle">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-1">
                                            <path d="M6 9l6 6l6 -6"></path>
                                        </svg>
                                    </div>
                                </button>
                                <div id="collapse-1-flush" className="accordion-collapse collapse show" data-bs-parent="#accordion-flush">
                                    <div className="accordion-body">
                                        <form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                loadData();
                                            }}
                                        >
                                            <div className="row">
                                                <div className="col-sm-3 form-group">
                                                    <label>Point de vente *</label>
                                                    <PdvSelect pointsDeVente={pointsDeVente} isPointsDeVenteLoading={isLoadingPointsDeVente || isFetchingPointsDeVente} selectedPdvId={selectedPdvId} setSelectedPdvId={setSelectedPdvId} />
                                                </div>
                                            </div>

                                            <div className="form-group mt-2">
                                                <button type="submit" className="btn btn-default" disabled={loading.generalData.isLoading || isLoadingShifts}>
                                                    {loading.generalData.isLoading ? (
                                                        <>
                                                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                            &nbsp; Générer
                                                        </>
                                                    ) : (
                                                        'Générer'
                                                    )}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card card-body">
                        <ul className="nav nav-tabs" id="myTab" role="tablist">
                            <li className="nav-item" role="presentation">
                                <button className="nav-link active" id="shifts-tab" data-bs-toggle="tab" data-bs-target="#shifts" type="button" role="tab" aria-controls="shifts" aria-selected="true">
                                    Shifts
                                </button>
                            </li>

                            <li className="nav-item" role="presentation">
                                <button className="nav-link" id="agents-tab" data-bs-toggle="tab" data-bs-target="#agents" type="button" role="tab" aria-controls="agents" aria-selected="false">
                                    Agents
                                </button>
                            </li>

                            <li className="nav-item" role="presentation">
                                <button className="nav-link" id="terminaux-tab" data-bs-toggle="tab" data-bs-target="#terminaux" type="button" role="tab" aria-controls="terminaux" aria-selected="false">
                                    Terminaux
                                </button>
                            </li>

                            <li className="nav-item" role="presentation">
                                <button className="nav-link" id="pompes-tab" data-bs-toggle="tab" data-bs-target="#pompes" type="button" role="tab" aria-controls="pompes" aria-selected="false">
                                    Pompes
                                </button>
                            </li>

                            <li className="nav-item" role="presentation">
                                <button className="nav-link" id="pistolets-tab" data-bs-toggle="tab" data-bs-target="#pistolets" type="button" role="tab" aria-controls="pistolets" aria-selected="false">
                                    Pistolets
                                </button>
                            </li>
                        </ul>

                        <div className="tab-content" id="myTabContent">
                            <div className="tab-pane fade show active p-1" id="shifts" role="tabpanel" aria-labelledby="shifts-tab">
                                <div className="card">
                                    <div className="accordion accordion-flush" id="accordion-shifts">
                                        <div className="accordion-item">
                                            <button className="accordion-header" type="button" data-bs-toggle="collapse" data-bs-target="#collapse-1-shifts" aria-expanded="true">
                                                <div className="accordion-header-text">
                                                    <h4>Filtre</h4>
                                                </div>
                                                <div className="accordion-header-toggle">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-1">
                                                        <path d="M6 9l6 6l6 -6"></path>
                                                    </svg>
                                                </div>
                                            </button>
                                            <div id="collapse-1-shifts" className="accordion-collapse collapse show" data-bs-parent="#accordion-shifts">
                                                <div className="accordion-body">
                                                    <div>
                                                        <div>
                                                            <div className="row">
                                                                <div className="col-sm-2 form-group">
                                                                    <label htmlFor="DateDebut">Du</label>
                                                                    <input
                                                                        type="date"
                                                                        className="form-control"
                                                                        value={shiftStartDate ?? ''}
                                                                        onChange={(e) => {
                                                                            setShiftStartDate(e.target.value);
                                                                        }}
                                                                    />
                                                                </div>

                                                                <div className="col-sm-2 form-group">
                                                                    <label htmlFor="DateFin">Au</label>
                                                                    <input
                                                                        type="date"
                                                                        className="form-control"
                                                                        value={shiftEndDate ?? ''}
                                                                        onChange={(e) => {
                                                                            setShiftEndDate(e.target.value);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="row mt-2">
                                                                <div className="col-sm-3 form-group">
                                                                    <input type="checkbox" name="" id="shiftOnlyOpen" checked={shiftOnlyOpen} onChange={(e) => setShiftOnlyOpen(e.target.checked)} />
                                                                    <label htmlFor="shiftOnlyOpen">&nbsp;Uniquement ouvert</label>
                                                                </div>
                                                            </div>

                                                            <div className="form-group mt-2">
                                                                <button type="button" className="btn btn-default" disabled={loading.generalData.isLoading || isLoadingShifts} onClick={handleLoadShifts}>
                                                                    {loading.generalData.isLoading || isLoadingShifts ? (
                                                                        <>
                                                                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                                            &nbsp; Générer
                                                                        </>
                                                                    ) : (
                                                                        'Générer'
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <div className="card">
                                                    <div className="card-header">
                                                        <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs">
                                                            <li className="nav-item">
                                                                <a href="#tabs-form" className="nav-link active" data-bs-toggle="tab">
                                                                    Formulaire
                                                                </a>
                                                            </li>
                                                            <li className="nav-item ms-auto">
                                                                <a href="#tabs-data" className="nav-link" title="Données" data-bs-toggle="tab">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-database">
                                                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                                        <path d="M12 6m-8 0a8 3 0 1 0 16 0a8 3 0 1 0 -16 0" />
                                                                        <path d="M4 6v6a8 3 0 0 0 16 0v-6" />
                                                                        <path d="M4 12v6a8 3 0 0 0 16 0v-6" />
                                                                    </svg>
                                                                </a>
                                                            </li>
                                                        </ul>
                                                    </div>

                                                    <div className="card-body">
                                                        <div className="tab-content">
                                                            <div className="tab-pane active show" id="tabs-form">
                                                                <div className="mb-2 d-flex justify-content-between align-items-start flex-wrap gap-2">
                                                                    <div>
                                                                        <div className="row gy-2 gx-3 align-items-center">
                                                                            <div className="col-auto">
                                                                                <div className="input-group">
                                                                                    <span className="input-group-text">
                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-transfer-vertical">
                                                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                                                            <path d="M10 4v16l-6 -5.5" />
                                                                                            <path d="M14 20v-16l6 5.5" />
                                                                                        </svg>
                                                                                    </span>

                                                                                    <ShiftSelect items={shifts} isItemsLoading={isLoadingShifts || isFetchingShifts} setSelectedItemId={setSelectedShiftId} selectedItemId={selectedShiftId} />
                                                                                </div>
                                                                            </div>

                                                                            <div className="col-auto">
                                                                                <div className="input-group">
                                                                                    <span className="input-group-text">
                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-user">
                                                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                                                            <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
                                                                                            <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
                                                                                        </svg>
                                                                                    </span>

                                                                                    <AgentSelect agents={agents} isAgentsLoading={isLoadingAgents || isFetchingAgents} setSelectedItemId={setSelectedAgentId} selectedItemId={selectedAgentId} />
                                                                                </div>
                                                                            </div>

                                                                            <div className="col-auto">
                                                                                <button type="button" className="btn btn-6 w-100 btn-icon" aria-label="Actualiser" onClick={() => loadShiftData()} disabled={isLoadingShifts || loading.shiftData.isLoading}>
                                                                                    {isLoadingShifts || loading.shiftData.isLoading ? (
                                                                                        <>
                                                                                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                                                        </>
                                                                                    ) : (
                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-refresh">
                                                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                                                            <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
                                                                                            <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
                                                                                        </svg>
                                                                                    )}
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="card">
                                                                    <div className="card-header">
                                                                        <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs">
                                                                            <li className="nav-item">
                                                                                <a href="#tabs-pistolets" className="nav-link active" data-bs-toggle="tab">
                                                                                    Pistolets
                                                                                </a>
                                                                            </li>
                                                                        </ul>
                                                                    </div>
                                                                    <div className="card-body">
                                                                        <div className="tab-content">
                                                                            <div className="tab-pane active show" id="tabs-pistolets">
                                                                                {isLoadingDetailsShift ? (
                                                                                    <>
                                                                                        <p className="text-center">Chargement ...</p>
                                                                                        <SkeletonLoader />
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        {detailsShift.length > 0 ? (
                                                                                            <DetailShiftGrid items={detailsShift} />
                                                                                        ) : (
                                                                                            <div className="alert alert-warning" role="alert">
                                                                                                <div className="alert-icon">
                                                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                                                                                                        <path d="M12 9v4"></path>
                                                                                                        <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                                                                                                        <path d="M12 16h.01"></path>
                                                                                                    </svg>
                                                                                                </div>
                                                                                                Aucune donnée.
                                                                                            </div>
                                                                                        )}
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="tab-pane" id="tabs-data">
                                                                {isLoadingShifts ? (
                                                                    <>
                                                                        <p className="text-center">En cours de chargement</p>
                                                                        <SkeletonLoader />
                                                                    </>
                                                                ) : shifts.length > 0 ? (
                                                                    <ShiftGrid shifts={shifts} />
                                                                ) : (
                                                                    <div className="alert alert-warning" role="alert">
                                                                        <div className="alert-icon">
                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                                                                                <path d="M12 9v4"></path>
                                                                                <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                                                                                <path d="M12 16h.01"></path>
                                                                            </svg>
                                                                        </div>
                                                                        Aucune donnée.
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="tab-pane fade p-1" id="agents" role="tabpanel" aria-labelledby="agents-tab">
                                {isLoadingAgents ? (
                                    <>
                                        <p className="text-center">En cours de chargement</p>
                                        <SkeletonLoader />
                                    </>
                                ) : agents.length > 0 ? (
                                    <AgentGrid agents={agents} />
                                ) : (
                                    <div className="alert alert-warning" role="alert">
                                        <div className="alert-icon">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                                                <path d="M12 9v4"></path>
                                                <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                                                <path d="M12 16h.01"></path>
                                            </svg>
                                        </div>
                                        Aucune donnée.
                                    </div>
                                )}
                            </div>

                            <div className="tab-pane fade p-1" id="terminaux" role="tabpanel" aria-labelledby="terminaux-tab">
                                {isLoadingTerminaux ? (
                                    <>
                                        <p className="text-center">En cours de chargement</p>
                                        <SkeletonLoader />
                                    </>
                                ) : terminaux.length > 0 ? (
                                    <TerminalGrid terminals={terminaux} />
                                ) : (
                                    <div className="alert alert-warning" role="alert">
                                        <div className="alert-icon">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                                                <path d="M12 9v4"></path>
                                                <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                                                <path d="M12 16h.01"></path>
                                            </svg>
                                        </div>
                                        Aucune donnée.
                                    </div>
                                )}
                            </div>

                            <div className="tab-pane fade p-1" id="pompes" role="tabpanel" aria-labelledby="pompes-tab">
                                {isLoadingPompes ? (
                                    <>
                                        <p className="text-center">En cours de chargement</p>
                                        <SkeletonLoader />
                                    </>
                                ) : pompes.length > 0 ? (
                                    <PompeGrid pompes={pompes} />
                                ) : (
                                    <div className="alert alert-warning" role="alert">
                                        <div className="alert-icon">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                                                <path d="M12 9v4"></path>
                                                <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                                                <path d="M12 16h.01"></path>
                                            </svg>
                                        </div>
                                        Aucune donnée.
                                    </div>
                                )}
                            </div>

                            <div className="tab-pane fade p-1" id="pistolets" role="tabpanel" aria-labelledby="pistolets-tab">
                                {isLoadingPistolets ? (
                                    <>
                                        <p className="text-center">En cours de chargement</p>
                                        <SkeletonLoader />
                                    </>
                                ) : vPistolets.length > 0 ? (
                                    <VPistoletGrid pistolets={vPistolets} />
                                ) : (
                                    <div className="alert alert-warning" role="alert">
                                        <div className="alert-icon">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                                                <path d="M12 9v4"></path>
                                                <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                                                <path d="M12 16h.01"></path>
                                            </svg>
                                        </div>
                                        Aucune donnée.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

PointOfSaleMasterDataPage.Layout = DefaultLayout;

export default PointOfSaleMasterDataPage;
